import type { InternalRequest } from '../client.js';

// Assure — duty-of-care evidence packs.
// Spec: docs/superpowers/specs/2026-08-12-assure-design.md
//
// Types mirror server/src/assure/assure.service.ts's AssurePack verbatim. Every
// section is a discriminated SectionResult, because a section that fails to
// build must be visibly distinguishable from one that is genuinely empty — an
// evidence document that silently drops a section would imply the events did
// not happen.

export type AssureSectionResult<T> = { ok: true; data: T } | { ok: false; error: string };

export interface AssureCoverageGap {
  startMs: number;
  endMs: number;
  minutes: number;
}

export interface AssureCoverageSection {
  expectedMs: number;
  coveredMs: number;
  /**
   * NULL means "no shifts recorded, so coverage cannot be assessed" — never 0
   * and never 100. Rendering it as a number would be a fabricated denominator
   * in a document whose entire value is that it contains none.
   */
  coveragePct: number | null;
  shiftCount: number;
  fixCount: number;
  gapMinutesThreshold: number;
  gaps: AssureCoverageGap[];
}

export interface AssureWelfareSection {
  checkIns: Array<{ type: string; count: number }>;
  checkInTotal: number;
  sos: Array<{
    id: string;
    raisedAt: string;
    acknowledgedAt: string | null;
    /** null when never acknowledged — never 0, which would read as instant. */
    ackLatencyMs: number | null;
    status: string;
  }>;
}

export interface AssureExposureSection {
  byType: Array<{ eventType: string; count: number; totalDurationMs: number | null }>;
  total: number;
}

export interface AssureResponseSection {
  incidents: Array<{
    id: string;
    title: string;
    severity: string;
    status: string;
    raisedAt: string;
    resolvedAt: string | null;
    resolutionMs: number | null;
  }>;
  musters: Array<{
    id: string;
    name: string;
    declaredAt: string;
    state: string;
    stateAt: string | null;
    responseMs: number | null;
  }>;
}

export interface AssureCommunicationSection {
  byChannel: Array<{
    channel: string;
    sent: number;
    failed: number;
    skipped: number;
    queued: number;
  }>;
  total: number;
  failures: Array<{ channel: string; reason: string | null; at: string }>;
}

/**
 * A caveat about what the pack cannot show. `code` is what the UI translates
 * (an English honesty section inside an otherwise-Arabic document undermines
 * the whole premise, and Arabic is this product's default locale); `text` is
 * the English rendering the CSV export writes.
 */
export type AssureLimitationCode =
  | 'evidenceNotCompliance'
  | 'receiptsNotDelivery'
  | 'positionNotPresence'
  | 'noShifts'
  | 'noWorkforce'
  | 'noGuardian'
  | 'noBroadcast'
  | 'gapsFound'
  | 'sectionFailed';

export interface AssureLimitation {
  code: AssureLimitationCode;
  text: string;
  params?: Record<string, string | number>;
}

export interface AssurePack {
  subject: { userId: string; name: string | null };
  org: { id: string; name: string };
  period: { fromIso: string; toIso: string };
  generatedAtIso: string;
  generatedBy: { userId: string; name: string | null };
  coverage: AssureSectionResult<AssureCoverageSection>;
  welfare: AssureSectionResult<AssureWelfareSection>;
  exposure: AssureSectionResult<AssureExposureSection>;
  response: AssureSectionResult<AssureResponseSection>;
  communication: AssureSectionResult<AssureCommunicationSection>;
  /** Always non-empty — every pack carries its unconditional caveats. */
  limitations: AssureLimitation[];
}

export interface AssureSubject {
  id: string;
  name: string | null;
  email: string;
}

export function assureEndpoints(req: InternalRequest) {
  return {
    /** GET /api/assure/subjects — admin/manager only. */
    subjects: () => req.request<{ subjects: AssureSubject[] }>('/assure/subjects'),

    /** GET /api/assure/pack — admin/manager only. Window capped at 366 days. */
    pack: (subjectUserId: string, fromIso: string, toIso: string) =>
      req.request<AssurePack>(
        `/assure/pack?subjectUserId=${encodeURIComponent(subjectUserId)}` +
          `&from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
      ),

    /**
     * GET /api/assure/pack/export — CSV ONLY (there is no server-side PDF in
     * this codebase; the PDF renders client-side). Returns the URL, not a
     * fetch: the route requires the caller's Bearer token, which a plain
     * `<a href>` cannot carry — mirrors musters.exportUrl / incidents.exportUrl.
     */
    exportUrl: (subjectUserId: string, fromIso: string, toIso: string) =>
      `${req.getBaseUrl()}/assure/pack/export?subjectUserId=${encodeURIComponent(subjectUserId)}` +
      `&from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}&format=csv`,
  };
}
